import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, Plus, Search, Filter, X, Eye, Pencil, Trash2, Download,
  RefreshCw, AlertTriangle, CheckCircle, ChevronRight, BarChart3,
  ClipboardCheck, Scan, Box, ArrowDown, Settings, Save, History, Users
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { API, formatPrice, formatDate } from '../utils';
import { PackName } from '../../ui/PackName';
import DeleteConfirmModal from '../DeleteConfirmModal';

const StockView = ({ fetchAllData }) => {
    // Stock data state (fetched internally)
    const [stockItems, setStockItems] = useState([]);
    const [stockDashboard, setStockDashboard] = useState(null);
    const [products, setProducts] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [stockEntries, setStockEntries] = useState([]);
    const [rentalPacks, setRentalPacks] = useState([]);

const [stockTab, setStockTab] = useState('dashboard'); // dashboard, products, items, entries, new-entry
    const [showNewProduct, setShowNewProduct] = useState(false);
    const [showNewEntry, setShowNewEntry] = useState(false);
    const [showNewPack, setShowNewPack] = useState(false);
    const [itemFilter, setItemFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState(''); // Nouveau filtre par typologie
    const [selectedItemHistory, setSelectedItemHistory] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    
    // Delete confirmation modal
    const [deleteModal, setDeleteModal] = useState({ open: false, description: '', action: null });
    
    // New Product Form
    const [newProduct, setNewProduct] = useState({
      sku: '', name: '', category: 'INDOOR', pitch: '', dimensions: '',
      weight: '', brightness: '', price_month_ht: '', caution_unit: '', description: ''
    });
    
    // New Entry Form
    const [newEntry, setNewEntry] = useState({
      product_id: '', purchase_date: new Date().toISOString().split('T')[0],
      supplier_name: '', invoice_number: '', unit_price_ht: '',
      serial_numbers_text: '', condition_received: 'NEUF', warranty_months: '', notes: ''
    });

    const STATUS_LABELS = {
      'DISPONIBLE': { color: 'bg-green-500/20 text-green-400', label: 'Disponible' },
      'RESERVE': { color: 'bg-yellow-500/20 text-yellow-400', label: 'Réservé' },
      'EN_LOCATION': { color: 'bg-blue-500/20 text-blue-400', label: 'En location' },
      'EN_TRANSIT': { color: 'bg-purple-500/20 text-purple-400', label: 'En transit' },
      'EN_CONTROLE': { color: 'bg-orange-500/20 text-orange-400', label: 'En contrôle' },
      'EN_MAINTENANCE': { color: 'bg-red-500/20 text-red-400', label: 'En maintenance' },
      'SINISTRE': { color: 'bg-red-600/20 text-red-500', label: 'Sinistre' },
      'RETIRE': { color: 'bg-slate-500/20 text-slate-400', label: 'Retiré' }
    };

    const CATEGORY_LABELS = {
      'DALLE_LED': 'Dalle LED',
      'VIDEO_PROCESSEUR': 'Vidéo Processeur',
      'FLIGHT_CASE': 'Flight Case',
      'CABLE': 'Câble',
      'ACCESSOIRE': 'Accessoire',
      'INDOOR': 'Indoor',
      'OUTDOOR': 'Outdoor'
    };

    // Fetch stock data
    const fetchStockData = async () => {
      try {
        const [dashRes, itemsRes, productsRes, suppliersRes, entriesRes, packsRes] = await Promise.all([
          axios.get(`${API}/admin/stock-dashboard`, { withCredentials: true }),
          axios.get(`${API}/admin/stock-items`, { withCredentials: true }),
          axios.get(`${API}/admin/products`, { withCredentials: true }),
          axios.get(`${API}/admin/suppliers`, { withCredentials: true }),
          axios.get(`${API}/admin/stock-entries`, { withCredentials: true }),
          axios.get(`${API}/admin/packs`, { withCredentials: true })
        ]);
        setStockDashboard(dashRes.data);
        setStockItems(itemsRes.data || []);
        setProducts(productsRes.data || []);
        setSuppliers(suppliersRes.data || []);
        setStockEntries(entriesRes.data || []);
        setRentalPacks(packsRes.data || []);
      } catch (error) {
        console.error('Stock data fetch error:', error);
      }
    };

    useEffect(() => { fetchStockData(); }, []);

    const handleDeleteEntry = async (entryId, productName) => {
      setDeleteModal({
        open: true,
        description: `Supprimer l'entrée de stock "${productName}" et tous ses articles associés ?`,
        action: async () => {
          try {
            const res = await axios.delete(`${API}/admin/stock-entries/${entryId}`, { withCredentials: true });
            toast.success(`Entrée supprimée (${res.data.items_deleted} articles retirés)`);
            fetchStockData();
          } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
          }
        }
      });
    };

    const handleDeleteItem = async (itemId, serialNumber) => {
      setDeleteModal({
        open: true,
        description: `Supprimer l'article "${serialNumber}" ? Cette action est irréversible.`,
        action: async () => {
          try {
            await axios.delete(`${API}/admin/stock-items/${itemId}`, { withCredentials: true });
            toast.success(`Article "${serialNumber}" supprimé`);
            fetchStockData();
          } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
          }
        }
      });
    };

    const handleCreateProduct = async () => {
      try {
        await axios.post(`${API}/admin/products`, newProduct, { withCredentials: true });
        toast.success('Produit créé avec succès');
        setShowNewProduct(false);
        setNewProduct({ sku: '', name: '', category: 'INDOOR', pitch: '', dimensions: '', weight: '', brightness: '', price_month_ht: '', caution_unit: '', description: '' });
        fetchAllData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la création');
      }
    };

    const handleCreateEntry = async () => {
      const serialNumbers = newEntry.serial_numbers_text.split('\n').map(s => s.trim()).filter(s => s);
      if (serialNumbers.length === 0) {
        toast.error('Entrez au moins un numéro de série');
        return;
      }
      try {
        await axios.post(`${API}/admin/stock-entries`, {
          ...newEntry,
          serial_numbers: serialNumbers,
          unit_price_ht: parseFloat(newEntry.unit_price_ht) || 0,
          warranty_months: newEntry.warranty_months ? parseInt(newEntry.warranty_months) : null
        }, { withCredentials: true });
        toast.success(`${serialNumbers.length} article(s) ajouté(s) au stock`);
        setShowNewEntry(false);
        setNewEntry({ product_id: '', purchase_date: new Date().toISOString().split('T')[0], supplier_name: '', invoice_number: '', unit_price_ht: '', serial_numbers_text: '', condition_received: 'NEUF', warranty_months: '', notes: '' });
        fetchAllData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la création');
      }
    };

    const handleUpdateItemStatus = async (itemId, newStatus) => {
      try {
        await axios.put(`${API}/admin/stock-items/${itemId}/status`, { status: newStatus }, { withCredentials: true });
        toast.success('Statut mis à jour');
        fetchAllData();
      } catch (error) {
        toast.error('Erreur lors de la mise à jour');
      }
    };

    const filteredItems = stockItems.filter(item => {
      const matchesSearch = !itemFilter || 
        item.serial_number?.toLowerCase().includes(itemFilter.toLowerCase()) ||
        item.product_name?.toLowerCase().includes(itemFilter.toLowerCase());
      const matchesStatus = !statusFilter || item.status === statusFilter;
      const matchesCategory = !categoryFilter || item.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });

    const fetchItemHistory = async (itemId) => {
      setLoadingHistory(true);
      try {
        const response = await axios.get(`${API}/admin/stock-items/${itemId}/history`, {
          withCredentials: true
        });
        setSelectedItemHistory(response.data);
        setShowHistoryModal(true);
      } catch (error) {
        console.error('Error fetching item history:', error);
        toast.error('Erreur lors du chargement de l\'historique');
      } finally {
        setLoadingHistory(false);
      }
    };

    // Sub-navigation tabs
    const stockTabs = [
      { id: 'dashboard', label: 'Dashboard' },
      { id: 'packs', label: 'Packs' },
      { id: 'items', label: 'Articles' },
      { id: 'products', label: 'Catalogue SKU' },
      { id: 'entries', label: 'Entrées' }
    ];

    // Edit pack component quantities
    const [editingPack, setEditingPack] = useState(null);
    const [editComponents, setEditComponents] = useState([]);
    const [savingPack, setSavingPack] = useState(false);

    const openEditPack = (pack) => {
      setEditingPack(pack);
      setEditComponents((pack.components || []).map(c => ({ ...c })));
    };

    const updateComponentQty = (idx, qty) => {
      setEditComponents(prev => prev.map((c, i) => i === idx ? { ...c, quantity: Math.max(0, parseInt(qty) || 0) } : c));
    };

    const removeComponent = (idx) => {
      setEditComponents(prev => prev.filter((_, i) => i !== idx));
    };

    const addComponentToPack = (productId) => {
      if (!productId) return;
      if (editComponents.some(c => c.product_id === productId)) {
        toast.error('Ce composant est déjà dans le pack');
        return;
      }
      const product = products.find(p => p.id === productId);
      if (!product) return;
      setEditComponents(prev => [...prev, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        is_alternative: false,
        alternative_group: product.alternative_group || null,
      }]);
    };

    const savePack = async () => {
      setSavingPack(true);
      try {
        await axios.put(`${API}/admin/packs/${editingPack.id}`, {
          components: editComponents.filter(c => c.quantity > 0).map(c => ({
            product_id: c.product_id,
            quantity: c.quantity,
            is_alternative: c.is_alternative || false,
            alternative_group: c.alternative_group || null
          }))
        }, { withCredentials: true });
        toast.success('Pack modifié !');
        setEditingPack(null);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur');
      } finally {
        setSavingPack(false);
      }
    };

    const deletePack = async (packId, packName) => {
      if (!window.confirm(`Supprimer le pack "${packName}" ?`)) return;
      try {
        await axios.delete(`${API}/admin/packs/${packId}`, { withCredentials: true });
        toast.success('Pack supprimé');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur');
      }
    };
    const [newPack, setNewPack] = useState({
      name: '', surface: '', description: '', price_month_ht: '', caution_total: '', components: []
    });
    const [packComponent, setPackComponent] = useState({
      product_id: '', quantity: 1, is_alternative: false, alternative_group: ''
    });

    const handleAddPackComponent = () => {
      if (!packComponent.product_id) return;
      const product = products.find(p => p.id === packComponent.product_id);
      if (!product) return;
      
      setNewPack({
        ...newPack,
        components: [...newPack.components, {
          product_id: product.id,
          product_sku: product.sku,
          product_name: product.name,
          quantity: parseInt(packComponent.quantity) || 1,
          is_alternative: packComponent.is_alternative,
          alternative_group: packComponent.is_alternative ? packComponent.alternative_group : null
        }]
      });
      setPackComponent({ product_id: '', quantity: 1, is_alternative: false, alternative_group: '' });
    };

    const handleRemovePackComponent = (index) => {
      setNewPack({
        ...newPack,
        components: newPack.components.filter((_, i) => i !== index)
      });
    };

    const handleCreatePack = async () => {
      if (!newPack.name || newPack.components.length === 0) {
        toast.error('Nom et composants requis');
        return;
      }
      try {
        await axios.post(`${API}/admin/packs`, {
          ...newPack,
          price_month_ht: parseFloat(newPack.price_month_ht) || null,
          caution_total: parseFloat(newPack.caution_total) || null
        }, { withCredentials: true });
        toast.success('Pack créé avec succès');
        setShowNewPack(false);
        setNewPack({ name: '', surface: '', description: '', price_month_ht: '', caution_total: '', components: [] });
        fetchAllData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la création');
      }
    };

    return (
      <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Gestion du Stock</h2>
          <div className="flex gap-2">
            <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => setShowNewPack(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Pack
            </Button>
            <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => setShowNewProduct(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Produit
            </Button>
            <Button className="bg-[#DFFF00] hover:bg-[#c8e600] text-black" onClick={() => setShowNewEntry(true)} data-testid="new-stock-entry-btn">
              <Plus className="w-4 h-4 mr-2" />
              Entrée de Stock
            </Button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-2 border-b border-slate-800 pb-2">
          {stockTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setStockTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                stockTab === tab.id 
                  ? 'bg-[#DFFF00] text-black' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {stockTab === 'dashboard' && stockDashboard && (
          <div className="space-y-4">
            {/* Alerts */}
            {stockDashboard.alerts?.length > 0 && (
              <div className="space-y-2">
                {stockDashboard.alerts.map((alert, idx) => (
                  <div key={idx} className={`p-3 rounded-lg flex items-center gap-3 ${
                    alert.type === 'danger' ? 'bg-red-500/10 border border-red-500/30' :
                    alert.type === 'warning' ? 'bg-orange-500/10 border border-orange-500/30' :
                    'bg-blue-500/10 border border-blue-500/30'
                  }`}>
                    <AlertTriangle className={`w-5 h-5 ${
                      alert.type === 'danger' ? 'text-red-400' :
                      alert.type === 'warning' ? 'text-orange-400' : 'text-blue-400'
                    }`} />
                    <span className="text-sm text-slate-300">{alert.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                <p className="text-sm text-slate-400">Stock Total</p>
                <p className="text-3xl font-bold text-white">{stockDashboard.counts?.total || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                <p className="text-sm text-slate-400">Disponible</p>
                <p className="text-3xl font-bold text-green-400">{stockDashboard.counts?.available || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                <p className="text-sm text-slate-400">En Location</p>
                <p className="text-3xl font-bold text-blue-400">{stockDashboard.counts?.rented || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                <p className="text-sm text-slate-400">En Maintenance</p>
                <p className="text-3xl font-bold text-orange-400">{stockDashboard.counts?.in_maintenance || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                <p className="text-sm text-slate-400">Taux d'utilisation</p>
                <p className="text-3xl font-bold text-[#DFFF00]">{stockDashboard.utilization_rate || 0}%</p>
              </div>
            </div>

            {/* Values */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
                <p className="text-sm text-slate-400 mb-1">Valeur du Parc</p>
                <p className="text-2xl font-bold text-white">{formatPrice(stockDashboard.values?.total_park_value || 0)}</p>
                <p className="text-xs text-slate-500">Prix d'achat total HT</p>
              </div>
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
                <p className="text-sm text-slate-400 mb-1">Valeur en Location</p>
                <p className="text-2xl font-bold text-blue-400">{formatPrice(stockDashboard.values?.value_rented || 0)}</p>
                <p className="text-xs text-slate-500">Matériel chez clients</p>
              </div>
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
                <p className="text-sm text-slate-400 mb-1">Revenus Générés</p>
                <p className="text-2xl font-bold text-[#DFFF00]">{formatPrice(stockDashboard.values?.total_revenue || 0)}</p>
                <p className="text-xs text-slate-500">CA cumulé des articles</p>
              </div>
            </div>

            {/* Stock by Product */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h3 className="font-semibold text-white mb-4">Stock par Produit</h3>
              <div className="space-y-3">
                {stockDashboard.by_product?.map(product => (
                  <div key={product._id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm text-white font-medium">{product.product_name}</p>
                      <p className="text-xs text-slate-500">{product.product_sku}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm"><span className="text-green-400 font-bold">{product.available}</span> <span className="text-slate-500">dispo</span></p>
                        <p className="text-xs text-slate-500">{product.rented} en location / {product.total} total</p>
                      </div>
                      <div className="w-24 bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-green-400 h-2 rounded-full" 
                          style={{ width: `${product.total > 0 ? (product.available / product.total) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {(!stockDashboard.by_product || stockDashboard.by_product.length === 0) && (
                  <p className="text-center text-slate-500 py-4">Aucun article en stock</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Packs Tab */}
        {stockTab === 'packs' && (
          <div className="space-y-4">
            {/* Edit Pack Modal */}
            {editingPack && (
              <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditingPack(null)}>
                <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-5 border-b border-slate-700">
                    <h3 className="text-lg font-bold text-white">{editingPack.name}</h3>
                    <button onClick={() => setEditingPack(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="p-5 space-y-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase">Composants & Quantités</p>
                    {editComponents.map((comp, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-slate-700/50 rounded-lg p-3">
                        <div className="flex-1 text-sm text-slate-300">{comp.product_name} {comp.is_alternative && <span className="text-xs text-blue-400 ml-1">(alt)</span>}</div>
                        <input
                          type="number" min="0"
                          value={comp.quantity}
                          onChange={e => updateComponentQty(idx, e.target.value)}
                          className="w-16 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-center text-sm"
                          data-testid={`edit-comp-qty-${idx}`}
                        />
                        <button onClick={() => removeComponent(idx)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}

                    {/* Add component */}
                    <div className="pt-3 border-t border-slate-700">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Ajouter un composant</p>
                      <select
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                        data-testid="add-component-select"
                        value=""
                        onChange={(e) => { addComponentToPack(e.target.value); e.target.value = ''; }}
                      >
                        <option value="">— Sélectionner un produit —</option>
                        {products
                          .filter(p => !editComponents.some(c => c.product_id === p.id))
                          .map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name}{p.category ? ` · ${p.category}` : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 p-5 border-t border-slate-700">
                    <Button variant="outline" onClick={() => setEditingPack(null)} className="border-slate-600 text-slate-300">Annuler</Button>
                    <Button onClick={savePack} disabled={savingPack} className="bg-[#DFFF00] hover:bg-[#c8e600] text-black" data-testid="save-pack-btn">
                      {savingPack ? 'Sauvegarde...' : 'Sauvegarder'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Packs Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rentalPacks.map(pack => (
                <div key={pack.id} className="bg-slate-900 rounded-xl border border-slate-800 p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-white">{pack.name}</h3>
                      {pack.surface && <p className="text-[#DFFF00] font-medium">{pack.surface}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        pack.availability?.available > 0 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {pack.availability?.available || 0} dispo
                      </div>
                    </div>
                  </div>
                  
                  {pack.description && (
                    <p className="text-sm text-slate-400 mb-4">{pack.description}</p>
                  )}
                  
                  {/* Components */}
                  <div className="space-y-2 mb-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase">Composants</p>
                    {(() => {
                      const groups = {};
                      const standalone = [];
                      pack.components?.forEach(comp => {
                        if (comp.alternative_group) {
                          if (!groups[comp.alternative_group]) groups[comp.alternative_group] = [];
                          groups[comp.alternative_group].push(comp);
                        } else {
                          standalone.push(comp);
                        }
                      });
                      
                      return (
                        <>
                          {Object.entries(groups).map(([groupName, comps]) => (
                            <div key={groupName} className="bg-slate-800/50 rounded-lg p-2">
                              <p className="text-xs text-slate-500 mb-1">🔄 {groupName} (au choix)</p>
                              {comps.map((comp, idx) => (
                                <p key={idx} className="text-xs text-slate-300 pl-2">
                                  • {comp.quantity}x {comp.product_name}
                                </p>
                              ))}
                            </div>
                          ))}
                          {standalone.map((comp, idx) => (
                            <p key={idx} className="text-xs text-slate-300">
                              ✓ {comp.quantity}x {comp.product_name}
                            </p>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                  
                  {/* Price + Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-slate-800">
                    <div>
                      {pack.price_month_ht && (
                        <p className="text-lg font-bold text-white">{formatPrice(pack.price_month_ht)}<span className="text-sm text-slate-400">/mois</span></p>
                      )}
                      {pack.caution_total && (
                        <p className="text-xs text-slate-500">Caution: {formatPrice(pack.caution_total)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {pack.availability?.limiting_component && pack.availability?.available === 0 && (
                        <p className="text-xs text-red-400 mr-2">Limité par: {pack.availability.limiting_component}</p>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEditPack(pack)} className="border-slate-600 text-slate-300 hover:text-white" data-testid={`edit-pack-${pack.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deletePack(pack.id, pack.name)} className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`delete-pack-${pack.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {rentalPacks.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Aucun pack créé</p>
                <Button className="mt-4 bg-[#DFFF00] hover:bg-[#c8e600] text-black" onClick={() => setShowNewPack(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un pack
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Products Catalog Tab */}
        {stockTab === 'products' && (
          <div className="space-y-4">
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">SKU</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Nom</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Catégorie</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Pitch</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Prix/mois HT</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Caution</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Stock</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {products.map(product => (
                    <tr key={product.id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-sm text-[#DFFF00] font-mono">{product.sku}</td>
                      <td className="px-4 py-3 text-sm text-white">{product.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-400">{CATEGORY_LABELS[product.category] || product.category}</td>
                      <td className="px-4 py-3 text-sm text-slate-400">{product.pitch ? `${product.pitch}mm` : '-'}</td>
                      <td className="px-4 py-3 text-sm text-white">{product.price_month_ht ? formatPrice(product.price_month_ht) : '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-400">{product.caution_unit ? formatPrice(product.caution_unit) : '-'}</td>
                      <td className="px-4 py-3">
                        <span className="text-green-400 font-bold">{product.stock_available || 0}</span>
                        <span className="text-slate-500"> / {product.stock_total || 0}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {products.length === 0 && (
                <p className="text-center text-slate-500 py-8">Aucun produit. Créez votre premier SKU.</p>
              )}
            </div>
          </div>
        )}

        {/* Items Tab */}
        {stockTab === 'items' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Rechercher par N° série ou produit..." 
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                  value={itemFilter}
                  onChange={(e) => setItemFilter(e.target.value)}
                />
              </div>
              <select 
                className="bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Toutes les typologies</option>
                <option value="DALLE_LED">Dalle LED</option>
                <option value="VIDEO_PROCESSEUR">Vidéo Processeur</option>
                <option value="FLIGHT_CASE">Flight Case</option>
                <option value="CABLE">Câble</option>
                <option value="ACCESSOIRE">Accessoire</option>
              </select>
              <select 
                className="bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Tous les statuts</option>
                {Object.entries(STATUS_LABELS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
              {(categoryFilter || statusFilter || itemFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCategoryFilter('');
                    setStatusFilter('');
                    setItemFilter('');
                  }}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4 mr-1" />
                  Effacer filtres
                </Button>
              )}
            </div>

            {/* Filter Summary */}
            {(categoryFilter || statusFilter) && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-400">Filtres actifs:</span>
                {categoryFilter && (
                  <span className="bg-[#DFFF00]/20 text-[#DFFF00] px-2 py-1 rounded-full text-xs">
                    {CATEGORY_LABELS[categoryFilter] || categoryFilter}
                  </span>
                )}
                {statusFilter && (
                  <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[statusFilter]?.color}`}>
                    {STATUS_LABELS[statusFilter]?.label}
                  </span>
                )}
                <span className="text-slate-500">({filteredItems.length} résultat{filteredItems.length > 1 ? 's' : ''})</span>
              </div>
            )}

            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">N° Série</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Produit</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Typologie</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Statut</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Historique</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Client actuel</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">État</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Prix achat</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Modifier statut</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filteredItems.map(item => (
                    <tr key={item.id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-sm text-[#DFFF00] font-mono">{item.serial_number}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-white">{item.product_name}</p>
                        <p className="text-xs text-slate-500">{item.product_sku}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                          {CATEGORY_LABELS[item.category] || item.category || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_LABELS[item.status]?.color || 'bg-slate-500/20 text-slate-400'}`}>
                          {STATUS_LABELS[item.status]?.label || item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => fetchItemHistory(item.id)}
                          disabled={loadingHistory}
                          className="text-[#DFFF00] hover:text-[#c8e600] hover:bg-[#DFFF00]/10"
                        >
                          <History className="w-4 h-4 mr-1" />
                          Voir
                        </Button>
                      </td>
                      <td className="px-4 py-3">
                        {item.customer_name ? (
                          <div>
                            <p className="text-sm text-white">{item.customer_name}</p>
                            {item.customer_company && (
                              <p className="text-xs text-slate-500">{item.customer_company}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-400">{item.condition_current}</td>
                      <td className="px-4 py-3 text-sm text-white">{formatPrice(item.purchase_price_ht)}</td>
                      <td className="px-4 py-3">
                        <select 
                          className="bg-slate-700 border-none text-xs text-white rounded px-2 py-1"
                          value={item.status}
                          onChange={(e) => handleUpdateItemStatus(item.id, e.target.value)}
                        >
                          {Object.entries(STATUS_LABELS).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDeleteItem(item.id, item.serial_number)}
                          className="p-1.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Supprimer cet article"
                          data-testid={`delete-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {filteredItems.length === 0 && (
                <p className="text-center text-slate-500 py-8">Aucun article trouvé</p>
              )}
            </div>
          </div>
        )}

        {/* Entries Tab */}
        {stockTab === 'entries' && (
          <div className="space-y-4">
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Fournisseur</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">N° Facture</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Produit</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Qté</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Prix unit. HT</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Total HT</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {stockEntries.map(entry => (
                    <tr key={entry.id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-sm text-slate-400">{formatDate(entry.purchase_date)}</td>
                      <td className="px-4 py-3 text-sm text-white">{entry.supplier_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-400 font-mono">{entry.invoice_number || '-'}</td>
                      <td className="px-4 py-3 text-sm text-white">{entry.product_name}</td>
                      <td className="px-4 py-3 text-sm text-[#DFFF00] font-bold">{entry.quantity}</td>
                      <td className="px-4 py-3 text-sm text-slate-400">{formatPrice(entry.unit_price_ht)}</td>
                      <td className="px-4 py-3 text-sm text-white font-medium">{formatPrice(entry.total_ht)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDeleteEntry(entry.id, entry.product_name)}
                          className="p-1.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Supprimer cette entrée"
                          data-testid={`delete-entry-${entry.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {stockEntries.length === 0 && (
                <p className="text-center text-slate-500 py-8">Aucune entrée de stock</p>
              )}
            </div>
          </div>
        )}

        {/* Modal: New Product */}
        {showNewProduct && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowNewProduct(false)}>
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white mb-4">Nouveau Produit (SKU)</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">SKU *</label>
                    <Input className="bg-slate-800 border-slate-700 text-white" placeholder="LED-IND-P39" value={newProduct.sku} onChange={e => setNewProduct({...newProduct, sku: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Catégorie</label>
                    <select className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2" value={newProduct.category} onChange={e => setNewProduct({...newProduct, category: e.target.value})}>
                      <option value="DALLE_LED">Dalle LED</option>
                      <option value="VIDEO_PROCESSEUR">Vidéo Processeur</option>
                      <option value="FLIGHT_CASE">Flight Case</option>
                      <option value="CABLE">Câble</option>
                      <option value="ACCESSOIRE">Accessoire</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Nom commercial *</label>
                  <Input className="bg-slate-800 border-slate-700 text-white" placeholder="Écran LED Indoor P3.9 500x500" value={newProduct.name} onChange={e => setNewProduct({...newProduct, name: e.target.value})} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Pitch (mm)</label>
                    <Input type="number" step="0.1" className="bg-slate-800 border-slate-700 text-white" placeholder="3.9" value={newProduct.pitch} onChange={e => setNewProduct({...newProduct, pitch: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Dimensions</label>
                    <Input className="bg-slate-800 border-slate-700 text-white" placeholder="500x500mm" value={newProduct.dimensions} onChange={e => setNewProduct({...newProduct, dimensions: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Luminosité (nits)</label>
                    <Input type="number" className="bg-slate-800 border-slate-700 text-white" placeholder="5000" value={newProduct.brightness} onChange={e => setNewProduct({...newProduct, brightness: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Prix location/mois HT</label>
                    <Input type="number" className="bg-slate-800 border-slate-700 text-white" placeholder="50" value={newProduct.price_month_ht} onChange={e => setNewProduct({...newProduct, price_month_ht: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Caution unitaire</label>
                    <Input type="number" className="bg-slate-800 border-slate-700 text-white" placeholder="100" value={newProduct.caution_unit} onChange={e => setNewProduct({...newProduct, caution_unit: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Description</label>
                  <textarea className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 h-20" placeholder="Description du produit..." value={newProduct.description} onChange={e => setNewProduct({...newProduct, description: e.target.value})} />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button className="bg-[#DFFF00] hover:bg-[#c8e600] text-black flex-1" onClick={handleCreateProduct}>Créer le produit</Button>
                <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => setShowNewProduct(false)}>Annuler</Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: New Stock Entry */}
        {showNewEntry && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowNewEntry(false)}>
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white mb-4">Nouvelle Entrée de Stock</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Produit (SKU) *</label>
                  <select className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2" value={newEntry.product_id} onChange={e => setNewEntry({...newEntry, product_id: e.target.value})} data-testid="entry-product-select">
                    <option value="">Sélectionner un produit</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Date d'achat *</label>
                    <Input type="date" className="bg-slate-800 border-slate-700 text-white" value={newEntry.purchase_date} onChange={e => setNewEntry({...newEntry, purchase_date: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Prix unitaire HT *</label>
                    <Input type="number" className="bg-slate-800 border-slate-700 text-white" placeholder="500" value={newEntry.unit_price_ht} onChange={e => setNewEntry({...newEntry, unit_price_ht: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Fournisseur *</label>
                    <Input className="bg-slate-800 border-slate-700 text-white" placeholder="Nom du fournisseur" value={newEntry.supplier_name} onChange={e => setNewEntry({...newEntry, supplier_name: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">N° Facture</label>
                    <Input className="bg-slate-800 border-slate-700 text-white" placeholder="FAC-2024-001" value={newEntry.invoice_number} onChange={e => setNewEntry({...newEntry, invoice_number: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">État à réception</label>
                    <select className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2" value={newEntry.condition_received} onChange={e => setNewEntry({...newEntry, condition_received: e.target.value})}>
                      <option value="NEUF">Neuf</option>
                      <option value="BON">Bon état</option>
                      <option value="USE">Usé</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Garantie (mois)</label>
                    <Input type="number" className="bg-slate-800 border-slate-700 text-white" placeholder="24" value={newEntry.warranty_months} onChange={e => setNewEntry({...newEntry, warranty_months: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Numéros de série * (1 par ligne)</label>
                  <textarea 
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 h-32 font-mono text-sm" 
                    placeholder="SN-LED-2024-001&#10;SN-LED-2024-002&#10;SN-LED-2024-003"
                    value={newEntry.serial_numbers_text}
                    onChange={e => setNewEntry({...newEntry, serial_numbers_text: e.target.value})}
                    data-testid="entry-serial-numbers"
                  />
                  <p className="text-xs text-slate-500 mt-1">{newEntry.serial_numbers_text.split('\n').filter(s => s.trim()).length} article(s)</p>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Notes</label>
                  <textarea className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 h-16" placeholder="Notes internes..." value={newEntry.notes} onChange={e => setNewEntry({...newEntry, notes: e.target.value})} />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button className="bg-[#DFFF00] hover:bg-[#c8e600] text-black flex-1" onClick={handleCreateEntry} data-testid="submit-stock-entry">Enregistrer l'entrée</Button>
                <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => setShowNewEntry(false)}>Annuler</Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: New Pack */}
        {showNewPack && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowNewPack(false)}>
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white mb-4">Nouveau Pack</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Nom du pack *</label>
                    <Input className="bg-slate-800 border-slate-700 text-white" placeholder="Pack 7m²" value={newPack.name} onChange={e => setNewPack({...newPack, name: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Surface</label>
                    <Input className="bg-slate-800 border-slate-700 text-white" placeholder="7m²" value={newPack.surface} onChange={e => setNewPack({...newPack, surface: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Prix/mois HT</label>
                    <Input type="number" className="bg-slate-800 border-slate-700 text-white" placeholder="1999" value={newPack.price_month_ht} onChange={e => setNewPack({...newPack, price_month_ht: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">Caution totale</label>
                    <Input type="number" className="bg-slate-800 border-slate-700 text-white" placeholder="1999" value={newPack.caution_total} onChange={e => setNewPack({...newPack, caution_total: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Description</label>
                  <Input className="bg-slate-800 border-slate-700 text-white" placeholder="Description du pack..." value={newPack.description} onChange={e => setNewPack({...newPack, description: e.target.value})} />
                </div>

                {/* Add Components */}
                <div className="border-t border-slate-800 pt-4">
                  <label className="text-sm text-slate-400 block mb-2">Ajouter un composant</label>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <select className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 text-sm" value={packComponent.product_id} onChange={e => setPackComponent({...packComponent, product_id: e.target.value})}>
                        <option value="">Sélectionner...</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Input type="number" min="1" className="bg-slate-800 border-slate-700 text-white text-sm" placeholder="Qté" value={packComponent.quantity} onChange={e => setPackComponent({...packComponent, quantity: e.target.value})} />
                    </div>
                    <div className="col-span-2">
                      <Input className="bg-slate-800 border-slate-700 text-white text-sm" placeholder="Groupe alt." value={packComponent.alternative_group} onChange={e => setPackComponent({...packComponent, alternative_group: e.target.value, is_alternative: !!e.target.value})} />
                    </div>
                    <div className="col-span-3">
                      <Button className="w-full bg-[#DFFF00] hover:bg-[#c8e600] text-black text-sm" onClick={handleAddPackComponent}>
                        <Plus className="w-4 h-4 mr-1" />Ajouter
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">💡 "Groupe alt." = composants interchangeables (ex: "dalles" pour alternatives 50x50 ou 50x100)</p>
                </div>

                {/* Components List */}
                {newPack.components.length > 0 && (
                  <div className="border-t border-slate-800 pt-4">
                    <label className="text-sm text-slate-400 block mb-2">Composants du pack ({newPack.components.length})</label>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {newPack.components.map((comp, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            {comp.alternative_group && (
                              <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">🔄 {comp.alternative_group}</span>
                            )}
                            <span className="text-sm text-white">{comp.quantity}x {comp.product_name}</span>
                          </div>
                          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleRemovePackComponent(idx)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-6">
                <Button className="bg-[#DFFF00] hover:bg-[#c8e600] text-black flex-1" onClick={handleCreatePack}>Créer le pack</Button>
                <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => setShowNewPack(false)}>Annuler</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Item History Modal */}
      {showHistoryModal && selectedItemHistory && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-[#DFFF00]" />
                  Historique de l'article
                </h3>
                <p className="text-[#DFFF00] font-mono text-sm mt-1">
                  {selectedItemHistory.item?.serial_number}
                </p>
                <p className="text-slate-400 text-sm">{selectedItemHistory.item?.product_name}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistoryModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Statistics Cards */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-800 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#DFFF00]">
                    {selectedItemHistory.statistics?.total_rentals || 0}
                  </p>
                  <p className="text-xs text-slate-400">Locations</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-white">
                    {selectedItemHistory.statistics?.total_days_rented || 0}
                  </p>
                  <p className="text-xs text-slate-400">Jours loués</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-400">
                    {formatPrice(selectedItemHistory.statistics?.total_revenue_generated || 0)}
                  </p>
                  <p className="text-xs text-slate-400">Revenus générés</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-400">
                    {selectedItemHistory.statistics?.average_rental_duration || 0}j
                  </p>
                  <p className="text-xs text-slate-400">Durée moyenne</p>
                </div>
              </div>

              {/* Item Details */}
              <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#DFFF00]" />
                  Informations de l'article
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Catégorie</p>
                    <p className="text-white">{CATEGORY_LABELS[selectedItemHistory.item?.category] || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">État actuel</p>
                    <p className="text-white">{selectedItemHistory.item?.condition_current || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Statut</p>
                    <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[selectedItemHistory.item?.status]?.color}`}>
                      {STATUS_LABELS[selectedItemHistory.item?.status]?.label}
                    </span>
                  </div>
                  <div>
                    <p className="text-slate-400">Date d'achat</p>
                    <p className="text-white">{selectedItemHistory.stock_entry?.purchase_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Prix d'achat HT</p>
                    <p className="text-white">{formatPrice(selectedItemHistory.item?.purchase_price_ht)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Fournisseur</p>
                    <p className="text-white">{selectedItemHistory.stock_entry?.supplier_name || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Rental History */}
              <div className="mb-6">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#DFFF00]" />
                  Historique des locations ({selectedItemHistory.rental_history?.length || 0})
                </h4>
                {selectedItemHistory.rental_history?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedItemHistory.rental_history.map((rental, index) => (
                      <div key={index} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-white font-medium">{rental.customer_name || 'Client inconnu'}</p>
                            {rental.customer_company && (
                              <p className="text-slate-400 text-sm">{rental.customer_company}</p>
                            )}
                            <p className="text-slate-500 text-xs">{rental.customer_email}</p>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              rental.status === 'active' || rental.status === 'confirmed' 
                                ? 'bg-green-500/20 text-green-400' 
                                : rental.status === 'cancelled'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-slate-500/20 text-slate-400'
                            }`}>
                              {rental.status}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-700 grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-400 text-xs">Pack</p>
                            <p className="text-white"><PackName name={rental.pack_name} /></p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-xs">Début</p>
                            <p className="text-white">
                              {rental.rental_start_date 
                                ? new Date(rental.rental_start_date).toLocaleDateString('fr-FR')
                                : '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-xs">Durée</p>
                            <p className="text-white">{rental.duration_days ? `${rental.duration_days} jours` : '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-xs">Prix/mois</p>
                            <p className="text-[#DFFF00]">{formatPrice(rental.monthly_price)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm bg-slate-800/30 rounded-lg p-4 text-center">
                    Aucune location enregistrée pour cet article
                  </p>
                )}
              </div>

              {/* Inspection History */}
              {selectedItemHistory.inspection_history?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-[#DFFF00]" />
                    États des lieux ({selectedItemHistory.inspection_history.length})
                  </h4>
                  <div className="space-y-3">
                    {selectedItemHistory.inspection_history.map((insp, index) => (
                      <div key={index} className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              insp.type === 'departure' 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : 'bg-orange-500/20 text-orange-400'
                            }`}>
                              {insp.type === 'departure' ? 'Départ' : 'Retour'}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              insp.condition === 'excellent' ? 'bg-green-500/20 text-green-400' :
                              insp.condition === 'good' ? 'bg-blue-500/20 text-blue-400' :
                              insp.condition === 'fair' ? 'bg-yellow-500/20 text-yellow-400' :
                              insp.condition === 'damaged' ? 'bg-red-500/20 text-red-400' :
                              insp.condition_before || insp.condition_after ? 'bg-slate-700 text-slate-300' :
                              'bg-slate-700 text-slate-400'
                            }`}>
                              {insp.condition === 'excellent' ? 'Excellent' :
                               insp.condition === 'good' ? 'Bon' :
                               insp.condition === 'fair' ? 'Correct' :
                               insp.condition === 'damaged' ? 'Endommagé' :
                               insp.condition_before && insp.condition_after ? `${insp.condition_before} → ${insp.condition_after}` :
                               insp.condition_after || insp.condition_before || 'Non spécifié'}
                            </span>
                            {insp.is_functional === false && (
                              <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 font-medium">Non fonctionnel</span>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-slate-400 text-xs">
                              {insp.created_at ? new Date(insp.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                            </p>
                            {insp.pack_name && <p className="text-slate-500 text-xs">{insp.pack_name}</p>}
                            {insp.inspector && <p className="text-slate-500 text-xs">{insp.inspector}</p>}
                          </div>
                        </div>
                        {insp.notes && (
                          <p className="text-slate-400 text-sm mt-2 bg-slate-800/50 rounded px-3 py-2">{insp.notes}</p>
                        )}
                        {insp.photos && insp.photos.length > 0 && (
                          <div className="mt-3">
                            <p className="text-slate-500 text-xs mb-2">Photos ({insp.photos.length})</p>
                            <div className="flex flex-wrap gap-2">
                              {insp.photos.map((photo, pIndex) => (
                                <a
                                  key={pIndex}
                                  href={photo.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                  data-testid={`inspection-photo-${index}-${pIndex}`}
                                >
                                  <img
                                    src={photo.url}
                                    alt={photo.original_name || `Photo ${pIndex + 1}`}
                                    className="w-20 h-20 object-cover rounded-lg border border-slate-600 hover:border-[#DFFF00] transition-colors cursor-pointer"
                                  />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700 flex justify-end">
              <Button
                onClick={() => setShowHistoryModal(false)}
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, description: '', action: null })}
        onConfirm={() => deleteModal.action && deleteModal.action()}
        itemDescription={deleteModal.description}
      />
      </>
    );
  };

  // Shipping Calculator View

export default StockView;
